import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { DateSeries, SeriesAction } from '@/api/types';
import { endpoints } from '@/api';
import { errorMessage } from '@/api/errors';
import { qk, useConfig, useDateSeries } from '@/api/queries';
import { toast } from '@/stores/toasts';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { SERIES_STATUS, nextLabel, seriesLabel } from './series';

const DONE: Partial<Record<SeriesAction, string>> = {
  accept: 'It’s a weekly thing',
  skip: 'Skipped this week',
  pause: 'Paused. Nothing lost.',
  resume: 'Back on',
  stop: 'Weekly date stopped',
  decline: 'We’ve let them know',
};

/** One weekly date night: "Every Thursday, 9 pm" with its next date and controls. */
export function SeriesCard({ s }: { s: DateSeries }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<SeriesAction | null>(null);
  const act = async (a: SeriesAction) => {
    if (busy) return;
    setBusy(a);
    try {
      const v = await endpoints.seriesAction(s.id, a);
      qc.setQueryData<{ series: DateSeries[] }>(qk.dateSeries, (p) =>
        p ? { series: v.status === 'stopped' || v.status === 'declined' ? p.series.filter((x) => x.id !== v.id) : p.series.map((x) => (x.id === v.id ? v : x)) } : p,
      );
      void qc.invalidateQueries({ queryKey: qk.dateInvites });
      if (DONE[a]) toast.info(DONE[a]!);
    } catch (e) {
      toast.error('That didn’t go through', errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const next = nextLabel(s);
  const btn = (a: SeriesAction, label: string, variant: 'solid' | 'ghost' | 'outline' = 'ghost') => (
    <Button size="sm" variant={variant} accent={variant === 'solid' ? 'pop' : 'paper'} onClick={() => void act(a)} loading={busy === a} disabled={!!busy} data-testid={`series-${a}`}>
      {label}
    </Button>
  );
  return (
    <li className="p-4" data-testid="series-card" data-status={s.status} data-id={s.id}>
      <div className="flex items-center gap-3">
        <Avatar avatar={s.with.avatar} size={44} label={s.with.display_name} />
        <div className="min-w-0 flex-1">
          <p className="text-[16px] font-semibold leading-snug" data-testid="series-label">
            {seriesLabel(s)}
          </p>
          <p className="text-[13px] text-muted">
            With {s.with.display_name} · {s.length_min} min · <span className={s.status === 'active' ? 'font-semibold text-go' : ''}>{SERIES_STATUS[s.status]}</span>
            {next && s.status !== 'pending' ? ` · ${next}` : ''}
          </p>
        </div>
        {s.next_invite_id && s.status === 'active' && (
          <Link to={`/dates/${s.next_invite_id}`} className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-raised px-3.5 text-sm font-semibold hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg" data-testid="series-open-date">
            <Icon name="calendar" size={16} /> Plan
          </Link>
        )}
      </div>
      <div className="-ml-2 mt-2 flex flex-wrap gap-1 sm:pl-14">
        {s.status === 'pending' && s.role === 'partner' && (
          <>
            {btn('accept', 'Yes, every week', 'solid')}
            {btn('decline', 'Not now')}
          </>
        )}
        {s.status === 'pending' && s.role === 'proposer' && <span className="self-center text-[13px] text-muted">Waiting for {s.with.display_name} to say yes.</span>}
        {s.status === 'active' && (
          <>
            {btn('skip', 'Skip this week')}
            {btn('pause', 'Pause')}
          </>
        )}
        {s.status === 'paused' && btn('resume', 'Resume', 'outline')}
        {s.status !== 'pending' && btn('stop', 'Stop')}
        {s.status === 'pending' && s.role === 'proposer' && btn('stop', 'Cancel')}
      </div>
    </li>
  );
}

/** Friends → Dates: weekly date nights (SPEC_V9 §A5). */
export function SeriesSection() {
  const config = useConfig();
  const enabled = config.data?.features.weekly_dates !== false && config.data?.features.date_invites !== false;
  const q = useDateSeries(enabled);
  const list = q.data?.series ?? [];
  if (!enabled || list.length === 0) return null;
  return (
    <div data-testid="series-list">
      <p className="mb-2 text-[15px] font-semibold">Weekly date night</p>
      <ul className="divide-y divide-line rounded-card bg-surface">
        {list.map((s) => (
          <SeriesCard key={s.id} s={s} />
        ))}
      </ul>
    </div>
  );
}
